import Text from "common/Typography/Text.tsx";
import TextWithPrecision from "common/Text/TextWithPrecision.tsx";
import {
  ConsolidatedCoinData,
  PositionData,
  PrecisionType,
  WorkspaceType,
} from "@shared/types";
import {
  CONTRACTS_EXCHANGES,
  EXCHANGES,
  ICON_TYPES,
} from "@shared/common/constants.ts";
import ChangeTag from "common/Tags/ChangeTag.tsx";
import { toFixed } from "@shared/utils/numbers.ts";
import { PERCENT_PRECISION } from "@shared/features/tradingTerminal/common/constants.ts";
import { typography } from "@shared/common/Themes/typography.ts";
import React from "react";
import {
  CHANGE_LEVERAGE_FEATURE_FLAG,
  DISPLAY_CURRENCIES,
} from "common/constants.ts";
import styled, { useTheme } from "styled-components";
import { useSymbolsMetadata, useUser } from "@shared/utils/hooks/misc.ts";
import { useSelector } from "react-redux";
import {
  selectDisplayCurrency,
  selectWorkspace,
} from "@shared/slices/common.slice.ts";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions.ts";
import useAssetItem from "features/portfolio/common/useAssetItem.tsx";
import LiveSymbolPrice from "common/LiveComponents/LiveSymbolPrice.tsx";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon.tsx";
import { getAssetFromSymbol } from "@shared/utils/misc.ts";

interface UsePositionItemProps {
  fontSize?: string;
}

const usePositionItem = ({
  fontSize = typography.fontSizes.M,
}: UsePositionItemProps) => {
  const theme = useTheme();
  const { user } = useUser();
  const workspace = useSelector(selectWorkspace);
  const { getPrecision, applyPrecision } = usePrecisions();
  const { getMainCoin, getDisplayCoin } = useSymbolsMetadata();
  const { getPriceChange, getNewLookPriceTrend, openCoinExtraDetails } =
    useAssetItem({
      isPositions: true,
    });
  const displayCurrency = useSelector(selectDisplayCurrency);

  const noData = <Text type="secondary">N/A</Text>;

  const getExchange = (data?: AssetDataType) =>
    data && "exchange" in data ? data.exchange : data?.exchanges?.[0];

  const getMargin = (
    data?: AssetDataType,
    hideSuffix?: boolean,
    hideCurrencyValue?: boolean
  ) => {
    const exchange = getExchange(data);
    if (!data?.margin) return noData;

    const currencyValue =
      data.margin[displayCurrency === DISPLAY_CURRENCIES.BTC ? 1 : 2];
    const precision = getPrecision(
      displayCurrency === DISPLAY_CURRENCIES.BTC
        ? PrecisionType.BTC_AMOUNT
        : PrecisionType.USD_AMOUNT,
      data.symbol,
      exchange
    );
    return (
      <div>
        {!hideCurrencyValue && (
          <TextWithPrecisionAndIcon
            text={currencyValue}
            iconType={ICON_TYPES.DISPLAY_CURRENCY}
            precision={precision}
            textStyle={{ fontSize, color: theme.colors.primaryText }}
            iconStyle={{ fontSize }}
          />
        )}
        <Text
          type={hideCurrencyValue ? "primary" : "secondary"}
          as="div"
          $size={fontSize}
        >
          {applyPrecision(
            data.margin[0],
            PrecisionType.AMOUNT,
            data?.symbol,
            exchange
          )}
          {!hideSuffix && <Suffix>{getMainCoin(data.symbol, exchange)}</Suffix>}
        </Text>
      </div>
    );
  };

  const getPositionSize = (data?: AssetDataType, hideSuffix?: boolean) => {
    const exchange = getExchange(data);
    return data?.position_size ? (
      <Text $size={fontSize}>
        {applyPrecision(
          data.position_size,
          PrecisionType.AMOUNT,
          data?.symbol,
          exchange
        )}
        {!hideSuffix && (
          <Suffix>{getDisplayCoin(data.symbol, exchange)}</Suffix>
        )}
      </Text>
    ) : (
      noData
    );
  };

  const getQuantity = (data?: AssetDataType) => {
    const exchange = getExchange(data);
    const precision = getPrecision(
      PrecisionType.QUANTITY,
      data?.symbol,
      exchange
    );
    return (
      <Text type="secondary" as="div" $size={fontSize}>
        <TextWithPrecision
          text={data?.quantity}
          precision={precision}
          round={true}
        />
        {data?.symbol && (
          <Suffix>
            {CONTRACTS_EXCHANGES.has(exchange || "")
              ? "Cont"
              : getAssetFromSymbol(data?.symbol)}
          </Suffix>
        )}
      </Text>
    );
  };

  const getUnrealizedPnl = (data?: AssetDataType, hideSuffix?: boolean) => {
    const exchange = getExchange(data);
    return data?.unrealized_pnl ? (
      <Text $size={fontSize}>
        {applyPrecision(
          data.unrealized_pnl,
          PrecisionType.AMOUNT,
          data?.symbol,
          exchange
        )}
        {!hideSuffix && (
          <Suffix>{getDisplayCoin(data.symbol, exchange)}</Suffix>
        )}
      </Text>
    ) : (
      noData
    );
  };

  const getROI = (data?: AssetDataType) => {
    const exchange = getExchange(data);
    const margin =
      exchange === EXCHANGES.BITGET_INVERSE
        ? data?.margin?.[2]
        : data?.margin?.[0];
    return margin && data?.unrealized_pnl !== undefined ? (
      <ChangeTag
        value={toFixed((data.unrealized_pnl / margin) * 100, PERCENT_PRECISION)}
        showArrow={false}
        suffix="%"
        textStyle={{ fontSize: typography.fontSizes.S }}
        style={{ marginLeft: 0 }}
      />
    ) : undefined;
  };

  const getEntryPrice = (data?: AssetDataType) => {
    const exchange = getExchange(data);
    const precision = getPrecision(PrecisionType.PRICE, data?.symbol, exchange);

    return data?.entry_price ? (
      <Text $size={fontSize}>
        <TextWithPrecision text={data.entry_price} precision={precision} />
      </Text>
    ) : (
      noData
    );
  };

  const getPrice = (data?: AssetDataType) => {
    return (
      <div style={{ minWidth: 100 }}>
        <LiveSymbolPrice
          symbol={data?.symbol}
          hideIcon={true}
          exchange={getExchange(data)}
          initialPrice={data?.price || 0}
          fontSize={fontSize}
        />
      </div>
    );
  };

  const getLiquidationPrice = (data?: AssetDataType) => {
    const exchange = getExchange(data);
    const precision = getPrecision(PrecisionType.PRICE, data?.symbol, exchange);
    return data?.liquidation_price ? (
      <Text style={{ color: theme.colors.orange }} as="div" $size={fontSize}>
        <TextWithPrecision
          text={data.liquidation_price}
          precision={precision}
        />
      </Text>
    ) : (
      noData
    );
  };

  const showEditLeverage = (coinData: ConsolidatedCoinData) =>
    workspace !== WorkspaceType.DEMO &&
    user?.hasFeatureFlag(CHANGE_LEVERAGE_FEATURE_FLAG) &&
    coinData.exchanges?.[0] !== EXCHANGES.FTX_FUTURES &&
    !user?.is_sub_user;

  return {
    getMargin,
    getPositionSize,
    getQuantity,
    getUnrealizedPnl,
    getROI,
    getEntryPrice,
    getLiquidationPrice,
    getPrice,
    getPriceChange,
    getNewLookPriceTrend,
    openCoinExtraDetails,
    showEditLeverage,
  };
};

export default usePositionItem;

type AssetDataType = PositionData | ConsolidatedCoinData;

const Suffix = styled.span`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  margin-left: 4px;
`;
